import { exactRoot, rootToPlaces } from "@/lib/tool-kit/arithmetic/roots";
import { absBig, BIG_ONE, BIG_TWO, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import { makeRational, type Rational } from "@/lib/tool-kit/math/rational";

/**
 * Powers of rational numbers, exact wherever the answer is rational.
 *
 * A whole-number exponent gives an exact fraction (or integer) by BigInt arithmetic. A
 * fractional exponent p/q is a root followed by a power; when the base is a perfect q-th
 * power the answer is still exact, and otherwise the digits come from an integer root so
 * they are right to the last place. Only when the result is astronomically large does
 * the module fall back to a logarithm for an order of magnitude.
 */
export type PowerResult =
  | { kind: "exact"; value: Rational }
  | { kind: "irrational"; negative: boolean }
  | { kind: "huge"; negative: boolean; log10: number }
  | { kind: "complex" }
  | { kind: "undefined" };

/** Largest exact result, in digits of numerator plus denominator. */
export const MAX_EXACT_DIGITS = 20_000;
/** Largest digit count we hand to an integer root. */
const MAX_ROOT_DIGITS = 30_000;
const LOG_HEAD = 17;
const TRAILING_ZEROS = /0+$/;

const ZERO: Rational = { num: BIG_ZERO, den: BIG_ONE };
const ONE: Rational = { num: BIG_ONE, den: BIG_ONE };

/** log10 of a positive integer, accurate to double precision at any size. */
export function log10Big(n: bigint): number {
  const digits = n.toString();
  if (digits.length <= LOG_HEAD) {
    return Math.log10(Number(digits));
  }
  return (
    Math.log10(Number(digits.slice(0, LOG_HEAD))) + (digits.length - LOG_HEAD)
  );
}

/** log10 |value| of a nonzero rational. */
export function log10Rational(value: Rational): number {
  return log10Big(absBig(value.num)) - log10Big(value.den);
}

function digitCount(n: bigint): number {
  return absBig(n).toString().length;
}

function isOdd(n: bigint): boolean {
  return n % BIG_TWO !== BIG_ZERO;
}

function integerPower(base: Rational, n: bigint): PowerResult {
  if (base.num === BIG_ZERO) {
    if (n > BIG_ZERO) {
      return { kind: "exact", value: ZERO };
    }
    return n === BIG_ZERO
      ? { kind: "exact", value: ONE }
      : { kind: "undefined" };
  }
  if (n === BIG_ZERO || (base.num === BIG_ONE && base.den === BIG_ONE)) {
    return { kind: "exact", value: ONE };
  }
  const negativeResult = base.num < BIG_ZERO && isOdd(n);
  if (absBig(base.num) === BIG_ONE && base.den === BIG_ONE) {
    return {
      kind: "exact",
      value: negativeResult ? makeRational(-BIG_ONE) : ONE,
    };
  }
  const size = absBig(n);
  const perFactor = log10Big(absBig(base.num)) + log10Big(base.den);
  const estimate = Number(size) * perFactor + 2;
  if (estimate > MAX_EXACT_DIGITS) {
    return {
      kind: "huge",
      negative: negativeResult,
      log10: Number(n) * log10Rational(base),
    };
  }
  const exponent = absBig(n);
  const top = base.num ** exponent;
  const bottom = base.den ** exponent;
  return {
    kind: "exact",
    value: n > BIG_ZERO ? makeRational(top, bottom) : makeRational(bottom, top),
  };
}

function fractionalPower(base: Rational, exponent: Rational): PowerResult {
  const p = exponent.num;
  const q = Number(exponent.den);
  if (base.num === BIG_ZERO) {
    return p > BIG_ZERO
      ? { kind: "exact", value: ZERO }
      : { kind: "undefined" };
  }
  const negativeBase = base.num < BIG_ZERO;
  if (negativeBase && q % 2 === 0) {
    return { kind: "complex" };
  }
  const negative = negativeBase && isOdd(p);
  const magnitude = { num: absBig(base.num), den: base.den };
  if (digitCount(magnitude.num) + digitCount(magnitude.den) > MAX_ROOT_DIGITS) {
    return {
      kind: "huge",
      negative,
      log10: (Number(p) / q) * log10Rational(magnitude),
    };
  }
  const rootNum = exactRoot(magnitude.num, q);
  const rootDen = exactRoot(magnitude.den, q);
  if (rootNum === null || rootDen === null) {
    return { kind: "irrational", negative };
  }
  const rooted = integerPower(makeRational(rootNum, rootDen), p);
  if (rooted.kind !== "exact") {
    return rooted;
  }
  return {
    kind: "exact",
    value: negative
      ? makeRational(-rooted.value.num, rooted.value.den)
      : rooted.value,
  };
}

/** base^exponent, in the real numbers. */
export function power(base: Rational, exponent: Rational): PowerResult {
  if (exponent.den === BIG_ONE) {
    return integerPower(base, exponent.num);
  }
  return fractionalPower(base, exponent);
}

/**
 * Decimal digits of an irrational real power, rounded half up at `places`. Null when the
 * numbers involved are too large to root exactly.
 */
export function powerDecimal(
  base: Rational,
  exponent: Rational,
  places: number
): string | null {
  const p = absBig(exponent.num);
  const q = Number(exponent.den);
  const num = absBig(base.num);
  const [top, bottom] =
    exponent.num >= BIG_ZERO ? [num, base.den] : [base.den, num];
  const size = (digitCount(top) + digitCount(bottom)) * Number(p) + q * places;
  if (size > MAX_ROOT_DIGITS) {
    return null;
  }
  return rootToPlaces(top ** p, bottom ** p, q, places).text;
}

/* -------------------------------------------------------------- display */

export interface ScientificParts {
  mantissa: string;
  exponent: number;
}

/** A positive integer's digits as d.ddd × 10^e, rounded half up to `sig` figures. */
export function scientificFromDigits(
  digits: string,
  sig: number
): ScientificParts {
  const exponent = digits.length - 1;
  if (digits.length <= sig) {
    const rest = digits.slice(1).replace(TRAILING_ZEROS, "");
    return {
      mantissa: rest === "" ? digits[0] : `${digits[0]}.${rest}`,
      exponent,
    };
  }
  let kept = BigInt(digits.slice(0, sig));
  if (Number(digits[sig]) >= 5) {
    kept += BIG_ONE;
  }
  let text = kept.toString();
  let adjusted = exponent;
  if (text.length > sig) {
    text = text.slice(0, sig);
    adjusted += 1;
  }
  const rest = text.slice(1).replace(TRAILING_ZEROS, "");
  return {
    mantissa: rest === "" ? text[0] : `${text[0]}.${rest}`,
    exponent: adjusted,
  };
}

/** d.ddd × 10^e from a base-10 logarithm, to `sig` figures. */
export function scientificFromLog10(
  log10: number,
  sig: number
): ScientificParts {
  let exponent = Math.floor(log10);
  let mantissa = 10 ** (log10 - exponent);
  const factor = 10 ** (sig - 1);
  mantissa = Math.round(mantissa * factor) / factor;
  if (mantissa >= 10) {
    mantissa /= 10;
    exponent += 1;
  }
  return { mantissa: String(mantissa), exponent };
}

/* ----------------------------------------------------------- exponent laws */

export type LawId = "product" | "quotient" | "power" | "negative" | "zero";

export interface LawRow {
  id: LawId;
  /** The exponents involved, as integers. */
  exponents: bigint[];
  /** The two sides, evaluated exactly. */
  left: Rational;
  right: Rational;
  /** The factors on the left before they are combined, where there are two. */
  leftParts: Rational[];
}

function exactOrNull(base: Rational, n: bigint): Rational | null {
  const result = integerPower(base, n);
  return result.kind === "exact" ? result.value : null;
}

/**
 * The exponent laws checked on the reader's own numbers, with whole-number exponents.
 * Null for a law whose values would be too large (or undefined, as 0^−1 is).
 */
export function exponentLaws(base: Rational, m: bigint, n: bigint): LawRow[] {
  const rows: LawRow[] = [];
  const bm = exactOrNull(base, m);
  const bn = exactOrNull(base, n);
  const bSum = exactOrNull(base, m + n);
  const bDiff = exactOrNull(base, m - n);
  const bProd = exactOrNull(base, m * n);
  const bNegN = exactOrNull(base, -n);
  if (bm && bn && bSum) {
    rows.push({
      id: "product",
      exponents: [m, n],
      left: makeRational(bm.num * bn.num, bm.den * bn.den),
      right: bSum,
      leftParts: [bm, bn],
    });
  }
  if (bm && bn && bDiff && bn.num !== BIG_ZERO) {
    rows.push({
      id: "quotient",
      exponents: [m, n],
      left: makeRational(bm.num * bn.den, bm.den * bn.num),
      right: bDiff,
      leftParts: [bm, bn],
    });
  }
  const inner = exactOrNull(base, m);
  const outer = inner ? exactOrNull(inner, n) : null;
  if (outer && bProd) {
    rows.push({
      id: "power",
      exponents: [m, n],
      left: outer,
      right: bProd,
      leftParts: inner ? [inner] : [],
    });
  }
  if (bn && bNegN && bn.num !== BIG_ZERO) {
    rows.push({
      id: "negative",
      exponents: [n],
      left: bNegN,
      right: makeRational(bn.den, bn.num),
      leftParts: [bn],
    });
  }
  if (base.num !== BIG_ZERO) {
    rows.push({
      id: "zero",
      exponents: [BIG_ZERO],
      left: ONE,
      right: ONE,
      leftParts: [],
    });
  }
  return rows;
}
